import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { useAuth } from "../../providers/AuthProvider";
import {
  ICohort,
  ICohortCreate,
  ICohortIndividualCreate,
  ICohortWorkingForm,
} from "../../routes/registries/models/Cohort";
import { useCreateCohort } from "../../services/api/cohorts/mutations/useCreateCohort";
import { useCreateCohortIndividuals } from "../../services/api/cohorts/mutations/useCreateCohortIndividuals";
import { useListCohorts } from "../../services/api/cohorts/queries/useListCohorts";
import { SingleCheckbox } from "../forms";
import { LibFullWidthSpinner } from "../lib";
import { CohortInlineForm } from "./CohortInlineForm";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  individuals: ICohortIndividualCreate[];
  excludeCohortId?: string;
}

export const AddToCohortModal = ({
  onClose,
  isOpen,
  individuals,
  excludeCohortId,
}: IProps) => {
  const [newCohortTitle, setNewCohortTitle] = React.useState<string>("");
  const [selectedCohortIds, setSelectedCohortIds] = React.useState<string[]>(
    []
  );

  const { userProfile } = useAuth();
  const toast = useToast();

  const { data: dataCohorts, isLoading: isLoadingCohorts } = useListCohorts();
  const {
    mutateAsync: mutateAsyncCohorts,
    isLoading: isLoadingCreateCohort,
    isError: isErrorCreateCohort,
  } = useCreateCohort();

  const {
    mutateAsync: mutateAsyncCohortInds,
    isLoading: isLoadingCreateCohortInds,
    // isError: isErrorCreateCohortInds,
  } = useCreateCohortIndividuals();

  const handleChangeNewCohortName = React.useCallback(
    (formIn: ICohortWorkingForm) => {
      setNewCohortTitle(formIn?.title || "");
    },
    [setNewCohortTitle]
  );

  const handleOnToggleCohortChecked = (cohort: ICohort) => {
    const indexOfCohort = selectedCohortIds.indexOf(cohort.id);
    if (indexOfCohort === -1) {
      setSelectedCohortIds([...selectedCohortIds, cohort.id]);
    } else {
      setSelectedCohortIds(selectedCohortIds.filter((id) => id !== cohort.id));
    }
  };

  const handleOnClose = () => {
    setSelectedCohortIds([]);
    onClose();
  };

  const handleOnAdd = async () => {
    let isAssignCohorts = true;
    let assignToCohorts = selectedCohortIds;

    if (newCohortTitle) {
      const createCohort: ICohortCreate = {
        title: newCohortTitle,
        user_id: userProfile.id,
      };

      // Create new cohort
      try {
        const resNewCohort = await mutateAsyncCohorts(createCohort);
        assignToCohorts.push(resNewCohort.id);
      } catch (error) {
        isAssignCohorts = false;
      }
    }

    if (isAssignCohorts) {
      await Promise.all(
        selectedCohortIds.map(async (cohortId) => {
          await mutateAsyncCohortInds({
            cohortId: cohortId,
            cohortIndividuals: individuals,
          });
        })
      );

      toast({
        title: "Added to Cohort",
        // description: `${individuals.length} individuals added to ${selectedCohortIds.length} cohorts`,
        description: `${selectedCohortIds.length} cohorts updated`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      handleOnClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      closeOnOverlayClick={true}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add individuals to cohort</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {isLoadingCohorts && (
              <Stack>
                {Array.from(Array(5), (e, i) => {
                  return (
                    <Skeleton
                      key={i}
                      height={"3rem"}
                      startColor={"gray.50"}
                      endColor={"gray.100"}
                    />
                  );
                })}
              </Stack>
            )}

            {!isLoadingCohorts && !dataCohorts?.length && (
              <Alert status="info" variant={"solid"}>
                <AlertIcon />
                <Text>
                  You dont have any existing cohorts, create a new one below
                </Text>
              </Alert>
            )}

            {!isLoadingCohorts && dataCohorts?.length && (
              <Stack spacing={8}>
                <Text color="text.softer">
                  Select the cohorts these records should be added to
                </Text>

                <Stack spacing={4}>
                  {dataCohorts
                    ?.filter((cohort) => cohort.id !== excludeCohortId)
                    .map((cohort) => {
                      return (
                        <HStack key={cohort.id} position={"relative"}>
                          <SingleCheckbox
                            _hover={{
                              bg: "bg.high",
                            }}
                            w="full"
                            py={2}
                            rounded="md"
                            px={4}
                            size="lg"
                            name={`${cohort.id}-checked`}
                            onChange={(e) =>
                              handleOnToggleCohortChecked(cohort)
                            }
                            isChecked={selectedCohortIds.includes(cohort.id)}
                          >
                            <Text ml={8}>{cohort.title}</Text>
                          </SingleCheckbox>
                        </HStack>
                      );
                    })}
                </Stack>
              </Stack>
            )}

            {!isLoadingCohorts && (
              <Stack>
                <HStack position={"relative"} spacing={8} py={2} px={4}>
                  <Box>
                    <SingleCheckbox
                      size="lg"
                      name="addToNew"
                      isDisabled={!newCohortTitle}
                      isChecked={!!newCohortTitle}
                    ></SingleCheckbox>
                  </Box>

                  <CohortInlineForm
                    name="title"
                    placeholder="New cohort name"
                    formControlProps={{
                      w: "24rem",
                    }}
                    onFormChange={handleChangeNewCohortName}
                  ></CohortInlineForm>

                  {isLoadingCreateCohort && (
                    <HStack spacing={4}>
                      <LibFullWidthSpinner size="md" thickness="2px" />
                      <Text color="text.softer">Creating cohort </Text>
                    </HStack>
                  )}
                </HStack>
              </Stack>
            )}

            {isErrorCreateCohort && (
              <Alert status="error" variant={"solid"}>
                <AlertIcon />
                <Text>Failed to create new cohort, please refresh</Text>
              </Alert>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup isDisabled={isLoadingCreateCohort}>
            <Button onClick={onClose}>Close</Button>
            <Button
              onClick={handleOnAdd}
              isDisabled={!selectedCohortIds.length && !newCohortTitle}
              colorScheme={"primary"}
              isLoading={isLoadingCreateCohort || isLoadingCreateCohortInds}
            >
              Add to cohort
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
